import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Header from '../elementos/basicos/Header';
import Footer from '../elementos/basicos/Footer';
import Boton from '../elementos/eventos/Boton';
import IconosGenerales from '../elementos/iconos/IconosGenerales';
import FormCursoArea from '../elementos/complejos/FormCursoArea';
import ImagenDefault from '../img/imgDefault.png';
import { getAreaPorSlug, getCursosDeArea } from '../../peticiones_API/Peticiones_Areas';
import { sanitizar, htmlEstaVacio } from '../../funciones/sanitizarHtml';

const API_URL = process.env.REACT_APP_API_URL;

const CursoAreaDetalle = () => {
  const { slug, cursoUrl } = useParams();
  const navigate = useNavigate();

  const [curso, setCurso] = useState(null);
  const [area, setArea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let activo = true;
    setLoading(true);
    setError(false);

    Promise.all([getAreaPorSlug(slug), getCursosDeArea(slug)])
      .then(([areaData, cursos]) => {
        if (!activo) return;
        const encontrado = (cursos || []).find((c) => c.url === cursoUrl);
        if (!encontrado) {
          toast.info('El curso que buscas ya no está disponible');
          navigate(`/areas/${slug}`);
          return;
        }
        setArea(areaData);
        setCurso(encontrado);
      })
      .catch((err) => {
        if (!activo) return;
        if (err?.response?.status === 404) {
          toast.info('El área que buscas ya no está disponible');
          navigate('/areas');
        } else {
          setError(true);
        }
      })
      .finally(() => { if (activo) setLoading(false); });

    return () => { activo = false; };
  }, [slug, cursoUrl, navigate]);

  if (loading) return (<><Header /><Estado>Cargando…</Estado><Footer /></>);
  if (error) return (<><Header /><Estado>No se pudo cargar el curso. Inténtalo de nuevo en unos minutos.</Estado><Footer /></>);
  if (!curso) return (<><Header /><Footer /></>);

  const imagenCurso = curso.imagen
    ? `${API_URL}/api/sepeformacion/public/cursos/imagen/${curso.imagen}`
    : ImagenDefault;

  // F3: solo se muestra el temario si hay PDF subido. Retirado el fallback a
  // temario_url (Synology): sin PDF, no se renderiza el botón "VER TEMARIO".
  const urlTemario = curso.temario_pdf
    ? `${API_URL}/api/sepeformacion/public/cursos/temario/${curso.temario_pdf}`
    : null;

  return (
    <>
      <Helmet>
        <title>{`${curso.nombre} | TusCursosSepe`}</title>
        <meta name="description" content={`Curso gratuito subvencionado: ${curso.nombre}.`} />
      </Helmet>
      <Header />

      <Contenedor>
        <FilaCursos>
          <Col lg="7">
            <EncabezadoCurso>
              <Volver onClick={() => navigate(`/areas/${slug}`)}>&larr; Volver al área</Volver>
              <h1>{curso.nombre}</h1>
              {(curso.ref || curso.ref_sepe) && (
                <Parrafo color="naranja">
                  {curso.ref ? `Ref. ${curso.ref}` : ''}{curso.ref && curso.ref_sepe ? ' · ' : ''}{curso.ref_sepe ? `Ref. SEPE ${curso.ref_sepe}` : ''}
                </Parrafo>
              )}
              {curso.convocatoria?.nombre && (
                <Convocatoria>{curso.convocatoria.nombre}</Convocatoria>
              )}
            </EncabezadoCurso>

            <CardCurso>
              {curso.horas != null && (
                <CardHeader><Card.Title><IconosGenerales id="reloj" />{curso.horas}h</Card.Title></CardHeader>
              )}
              <Card.Img variant="top" src={imagenCurso} alt={curso.nombre} onError={(e) => { e.target.src = ImagenDefault; }} />
              {urlTemario && (
                <CuerpoCard>
                  <IconosGenerales id="libro" curso="true" />
                  <Boton as="a" href={urlTemario} target="_blank" rel="noopener noreferrer" temario="true">VER TEMARIO</Boton>
                </CuerpoCard>
              )}
            </CardCurso>

            <SeccionRequisitos>
              <CabeceraRequisitos>Requisitos</CabeceraRequisitos>
              <CajaRequisitos>
                {htmlEstaVacio(curso.requisitos) ? (
                  <TextoSimple>No hay requisitos para alumnos</TextoSimple>
                ) : (
                  <ContenidoRequisitos dangerouslySetInnerHTML={{ __html: sanitizar(curso.requisitos) }} />
                )}
              </CajaRequisitos>
            </SeccionRequisitos>

            {!htmlEstaVacio(curso.observaciones_publicas) && (
              <SeccionRequisitos>
                <CabeceraRequisitos>Observaciones</CabeceraRequisitos>
                <CajaRequisitos>
                  <ContenidoRequisitos dangerouslySetInnerHTML={{ __html: sanitizar(curso.observaciones_publicas) }} />
                </CajaRequisitos>
              </SeccionRequisitos>
            )}
          </Col>

          <CuerpoCursos lg="5">
            <FormCursoArea curso={curso} area={area} />
          </CuerpoCursos>
        </FilaCursos>
      </Contenedor>

      <Footer />
    </>
  );
};

const Contenedor = styled(Container)`
  margin-top: 4%;
`;

const FilaCursos = styled(Row)`
  max-width: 90%;
  margin: 0 auto;
`;

const CuerpoCursos = styled(Col)`
  box-shadow: 0px 0px 10px 0px rgba(157, 157, 157, 0.5);
  margin: auto !important;
  margin-top: 70px !important;
  padding: 4% 3% 3% !important;

  @media screen and (max-width: 768px) {
    margin-left: 10% !important;
    margin-right: 100px !important;
  }
`;

const EncabezadoCurso = styled.div`
  h1 {
    font-family: 'Montserrat';
    font-weight: 1000;
    font-size: 28px;
    text-transform: uppercase;
  }
`;

const Volver = styled.button`
  border: none;
  background: none;
  color: #ee805d;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  padding: 0;
  margin-bottom: 0.6rem;
  cursor: pointer;
`;

const Parrafo = styled.p`
  color: ${(props) => (props.color === 'gris' ? '#7A7A7A' : props.color === 'naranja' ? '#EE805D' : 'black')};
  text-transform: capitalize;
  font-family: 'Montserrat';
  font-size: 17px;
  font-weight: 400;
  font-style: italic;
  line-height: 1.2em;
`;

const Convocatoria = styled.div`
  color: #666;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.85rem;
  font-weight: 500;
  margin-top: 2px;
`;

const CardCurso = styled(Card)`
  width: 90%;
  border: none !important;
`;

const CardHeader = styled(Card.Header)`
  border: none !important;
  margin-bottom: 10px !important;
`;

const CuerpoCard = styled(Card.Body)`
  margin-left: -20px !important;
`;

const Estado = styled.div`
  text-align: center;
  padding: 12vh 1rem;
  font-family: 'Montserrat', sans-serif;
  color: #888;
`;

const SeccionRequisitos = styled.div`
  width: 90%;
  margin-top: 1.5rem;
`;

const CabeceraRequisitos = styled.div`
  background-color: #EE805D;
  color: #fff;
  padding: 6px 14px;
  font-size: 18px;
  font-weight: 600;
  text-transform: uppercase;
  font-family: 'Montserrat', sans-serif;
  border-radius: 4px 4px 0 0;
`;

const CajaRequisitos = styled.div`
  border: 1px solid #ececec;
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 1.1rem 1.3rem;
  background: #fff;
`;

const TextoSimple = styled.p`
  margin: 0;
  color: #7A7A7A;
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
`;

const ContenidoRequisitos = styled.div`
  color: #4a4a4a;
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
  line-height: 1.6;

  p { margin: 0 0 0.6rem; }
  p:last-child { margin-bottom: 0; }

  ul, ol { margin: 0 0 0.6rem; padding-left: 1.3rem; }
  ul { list-style: disc; }
  ol { list-style: decimal; }
  li { margin-bottom: 0.25rem; }

  a {
    color: #EE805D;
    text-decoration: underline;
    font-weight: 600;
  }
  a:hover { color: #d96947; }
`;

export default CursoAreaDetalle;
